import { useEffect, useState } from 'react';
import { discoveryDestinationApi } from '../../api/discoveryDestinationApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import useDiscoverySuggestions from './useDiscoverySuggestions';

/** Fetch one level at a time; group keys are opaque, never guessed geo IDs. */
export default function JourneyDestinationPicker({ selected, onSelect }) {
  const { t, locale } = useTravelTranslation();
  const [ancestors, setAncestors] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const suggestions = useDiscoverySuggestions();
  const parentKey = ancestors.at(-1)?.key || '';
  const label = (node) => (String(locale).startsWith('zh') ? node.displayName : node.canonicalName) || node.displayName;
  useEffect(() => {
    const controller = new AbortController();
    setPhase('loading'); setError('');
    discoveryDestinationApi.list(parentKey, { signal: controller.signal }).then((raw) => {
      if (controller.signal.aborted) return;
      const payload = raw?.data ?? raw;
      setNodes(Array.isArray(payload?.items) ? payload.items : []); setPhase('ready');
    }).catch((cause) => {
      if (controller.signal.aborted) return;
      setError(cause?.message || t('travel.journeyDiscovery.destinationError')); setPhase('error');
    });
    return () => controller.abort();
  }, [parentKey, retry, t]);
  const choose = (node) => onSelect({ destinationKey: node.key, displayName: label(node), hasChildren: node.hasChildren });
  const searching = Boolean(suggestions.query.trim());
  return <section className="journey-destination-picker" aria-label={t('travel.journeyDiscovery.destination')}>
    <nav aria-label={t('travel.journeyDiscovery.destinationPath')}>
      <button type="button" onClick={() => { setAncestors([]); suggestions.clear(); }}>{t('travel.discover.filters.allLocations')}</button>
      {ancestors.map((node, index) => <button key={node.key} type="button"
        onClick={() => { setAncestors(ancestors.slice(0, index + 1)); suggestions.clear(); }}>{label(node)}</button>)}
    </nav>
    {selected ? <p>{t('travel.journeyDiscovery.selectedDestination')}: {selected.displayName}</p> : null}
    <label>{t('travel.journeyDiscovery.searchDestination')}
      <input className="discover-filters__input" value={suggestions.query}
        onChange={(e) => suggestions.onQueryChange(e.target.value)}
        onCompositionStart={suggestions.onCompositionStart}
        onCompositionEnd={(e) => suggestions.onCompositionEnd(e.target.value)} />
    </label>
    {searching ? <div aria-live="polite">
      {suggestions.phase === 'loading' ? <p>{t('travel.common.loading')}</p> : null}
      {suggestions.phase === 'error' ? <p role="alert">{t('travel.journeyDiscovery.destinationError')}</p> : null}
      {suggestions.phase === 'idle' ? <p>{t('travel.journeyDiscovery.destinationSearchHint')}</p> : null}
      {suggestions.phase === 'ready' && !suggestions.items.some(n => n.type === 'GEO') ? <p>{t('travel.journeyDiscovery.noDestinations')}</p> : null}
      {suggestions.items.filter(n => n.type === 'GEO').map(node => <button key={node.id} type="button"
        onClick={() => choose({ key: `geo:${node.id}`, displayName: node.label, canonicalName: node.label })}>{node.label}</button>)}
    </div> : <>
      {parentKey ? <button type="button" onClick={() => choose(ancestors.at(-1))}>{t('travel.journeyDiscovery.useScope')}: {label(ancestors.at(-1))}</button> : null}
      {phase === 'loading' ? <p role="status">{t('travel.common.loading')}</p> : null}
      {phase === 'error' ? <div role="alert">{error} <button type="button" onClick={() => setRetry(v => v + 1)}>{t('travel.common.retry')}</button></div> : null}
      {phase === 'ready' && nodes.length === 0 ? <p>{t('travel.journeyDiscovery.noDestinations')}</p> : null}
      {phase === 'ready' ? <ul>
        {nodes.map(node => <li key={node.key}>
          <span>{label(node)}</span>
          <div>
            <button type="button" aria-label={`${t('travel.journeyDiscovery.selectDestination')} ${label(node)}`}
              aria-pressed={selected?.destinationKey === node.key} onClick={() => choose(node)}>{t('travel.journeyDiscovery.selectDestination')}</button>
            {node.hasChildren ? <button type="button" aria-label={`${t('travel.journeyDiscovery.viewChildren')} ${label(node)}`}
              onClick={() => setAncestors([...ancestors, node])}>{t('travel.journeyDiscovery.viewChildren')}</button> : null}
          </div>
        </li>)}
      </ul> : null}
    </>}
  </section>;
}
